import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { Route } from "@/routes/st-admin/tickets/index";
import { useGetTickets, useGetTicketsCount } from "@/services";
import {
	Button,
	ErrorBoundary,
	Icon,
	Skeleton,
	TabItem,
	Tabs,
	TabsList,
	Typography,
} from "@st-marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { TicketCard, waitingFor } from "./TicketCard";

const PAGE_SIZE = 25;

/** Open tickets come urgent first, then the one waiting longest. */
const STAdminTickets = () => {
	const { t } = useTranslation();
	const { getErrorInterface } = useApiError();
	const navigate = useNavigate({ from: "/st-admin/tickets" });
	const { status = "OPEN", page = 1 } = Route.useSearch();

	const { data: count } = useGetTicketsCount();
	const { data, isLoading, error, refetch } = useGetTickets({
		query: { status, page, page_size: PAGE_SIZE },
	});
	const tickets = data?.results ?? [];
	const pages = Math.max(1, Math.ceil((data?.count ?? 0) / PAGE_SIZE));

	// Resolving the last tickets of the last page leaves it empty: step back.
	useEffect(() => {
		if (data && page > pages) {
			navigate({ search: (prev) => ({ ...prev, page: pages }), replace: true });
		}
	}, [data, page, pages, navigate]);

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-1">
				<Typography variant="title-md" as="h1">
					{t("admin.menuTickets")}
				</Typography>
				{count ? (
					<Typography
						variant="body-sm"
						as="p"
						className="text-secondary-text-100"
					>
						{count.open === 0
							? t("admin.tickets.allClear")
							: t("admin.tickets.summary", {
									open: count.open,
									urgent: count.urgent,
									time: count.oldest_open_at
										? waitingFor(count.oldest_open_at)
										: "-",
								})}
					</Typography>
				) : null}
			</div>

			<Tabs
				value={status}
				onValueChange={(value) =>
					navigate({
						search: { status: value as "OPEN" | "CLOSED" },
						replace: true,
					})
				}
			>
				<TabsList>
					<TabItem value="OPEN" Icon={<Icon name="alarm-warning-line" />}>
						{t("admin.tickets.open")}
						{count?.open ? ` (${count.open})` : ""}
					</TabItem>
					<TabItem value="CLOSED" Icon={<Icon name="checkbox-circle-line" />}>
						{t("admin.tickets.resolvedTab")}
					</TabItem>
				</TabsList>
			</Tabs>

			<ErrorBoundary
				isLoading={false}
				error={error ? getErrorInterface(error) : undefined}
				onReset={refetch}
			>
				<div className="flex flex-col gap-3">
					{isLoading ? (
						[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)
					) : tickets.length === 0 ? (
						<Typography
							variant="body-md"
							as="p"
							className="py-10 text-center text-secondary-text-100"
						>
							{status === "OPEN"
								? t("admin.tickets.allClear")
								: t("admin.tickets.noResolved")}
						</Typography>
					) : (
						tickets.map((ticket) => (
							<TicketCard key={ticket.id} ticket={ticket} />
						))
					)}
				</div>
			</ErrorBoundary>

			{pages > 1 ? (
				<div className="flex items-center justify-end gap-2">
					<Button
						type="button"
						size="sm"
						variant="text"
						disabled={page <= 1}
						onClick={() =>
							navigate({ search: (prev) => ({ ...prev, page: page - 1 }) })
						}
					>
						{t("admin.tickets.previous")}
					</Button>
					<Typography variant="body-sm">{`${page} / ${pages}`}</Typography>
					<Button
						type="button"
						size="sm"
						variant="text"
						disabled={page >= pages}
						onClick={() =>
							navigate({ search: (prev) => ({ ...prev, page: page + 1 }) })
						}
					>
						{t("admin.tickets.next")}
					</Button>
				</div>
			) : null}
		</div>
	);
};

export default STAdminTickets;
